// src/services/loginTwoFactorGate.ts — the ONE place a 403
// LOGIN_TWO_FACTOR_REQUIRED is turned into "ask for the code, then carry on".
//
// With sign-in two-factor enforced, every authenticated api route refuses a
// bearer that has not passed POST /auth/verify-2fa yet. That is not a
// permission denial: the operator is who they say, they owe one code. So the
// refusal must never reach /unauthorized or a "permission denied" toast.
//
// The pieces:
//   • the AuthProvider registers a RUNNER, which shows the code screen and
//     resolves once the code is accepted (or rejects if the operator signs out);
//   • every refused request asks for clearance through here, so two requests
//     refused at once share ONE screen;
//   • once cleared, listeners hear about it (the console socket reconnects).
//
// The console's own axios instance handles the refusal in its response
// interceptor (services/apiService.ts). The `axios.create()` clients that skip
// that interceptor install the same replay with `installLoginTwoFactorReplay`.
import axios, {
  type AxiosError,
  type AxiosInstance,
  type AxiosResponse,
} from "axios";

export const LOGIN_TWO_FACTOR_REQUIRED_CODE = "LOGIN_TWO_FACTOR_REQUIRED";

declare module "axios" {
  interface AxiosRequestConfig {
    /**
     * How a 403 LOGIN_TWO_FACTOR_REQUIRED on this request is handled:
     *   undefined   ask for the code, then replay the request once (default);
     *   "caller"    reject at once; the caller runs the code screen itself
     *               (AuthContext's bootstrap, which re-runs the whole sign-in);
     *   "replayed"  this IS the replay; a second refusal rejects, never a third try.
     */
    loginTwoFactor?: "caller" | "replayed";
  }
}

type Runner = () => Promise<void>;

let runner: Runner | null = null;
let inFlight: Promise<void> | null = null;
const clearedListeners = new Set<() => void>();

/** Registered by the AuthProvider while it is mounted; null to unregister. */
export const setLoginTwoFactorRunner = (next: Runner | null): void => {
  runner = next;
  // A screen that was open belongs to the runner being replaced, which settles
  // it itself; new refusals must ask the new one.
  inFlight = null;
};

export const isLoginTwoFactorRequired = (
  status: number | undefined,
  code: string | undefined
): boolean => status === 403 && code === LOGIN_TWO_FACTOR_REQUIRED_CODE;

/** Tell everyone waiting for clearance (the socket) that the code was accepted. */
export const notifyLoginTwoFactorCleared = (): void => {
  clearedListeners.forEach((listener) => listener());
};

export const onLoginTwoFactorCleared = (listener: () => void): (() => void) => {
  clearedListeners.add(listener);
  return () => {
    clearedListeners.delete(listener);
  };
};

/**
 * Resolves once the operator's code is accepted. Concurrent callers share the
 * same screen. Rejects when no screen is mounted or the operator abandons it.
 */
export const requestLoginTwoFactorClearance = (): Promise<void> => {
  if (!runner) {
    return Promise.reject(new Error("No sign-in code screen is available."));
  }
  if (!inFlight) {
    const pending: Promise<void> = runner()
      .then(() => notifyLoginTwoFactorCleared())
      .finally(() => {
        if (inFlight === pending) inFlight = null;
      });
    inFlight = pending;
  }
  return inFlight;
};

const blobText = (blob: Blob): Promise<string> =>
  typeof blob.text === "function"
    ? blob.text()
    : new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result ?? ""));
        reader.onerror = () => resolve("");
        reader.readAsText(blob);
      });

/**
 * The api's `code` on a refusal. A `responseType: "blob"` request (the CSV
 * exports) gets its JSON error body as a Blob, so read that too. The body
 * itself is left as it is: those callers parse it themselves.
 */
const refusalCodeOf = async (data: unknown): Promise<string | undefined> => {
  let body = data;
  if (typeof Blob !== "undefined" && data instanceof Blob) {
    try {
      body = JSON.parse(await blobText(data));
    } catch {
      return undefined;
    }
  }
  const code = (body as { code?: unknown } | null | undefined)?.code;
  return typeof code === "string" ? code : undefined;
};

/** Whether a request refused for the login factor should wait for the code and replay. */
export const shouldReplayAfterLoginTwoFactor = async (
  error: AxiosError
): Promise<boolean> => {
  if (
    runner === null ||
    error.config === undefined ||
    error.config.loginTwoFactor !== undefined ||
    error.response?.status !== 403
  ) {
    return false;
  }
  return isLoginTwoFactorRequired(
    403,
    await refusalCodeOf(error.response.data)
  );
};

/**
 * Send a request refused for the login factor again — once (it is marked as
 * the replay), and with the console's CURRENT bearer, the one the code screen
 * just cleared. Call only after `requestLoginTwoFactorClearance` resolved.
 */
export const replayRequest = <T>(
  client: AxiosInstance,
  error: AxiosError
): Promise<AxiosResponse<T>> => {
  const config = error.config!;
  // Set by AuthContext after sign-in (services/apiService setAuthorization).
  const authorization = axios.defaults.headers.common["Authorization"];
  if (typeof authorization === "string" && authorization) {
    config.headers.set("Authorization", authorization);
  }
  return client.request<T>({ ...config, loginTwoFactor: "replayed" });
};

/**
 * For an `axios.create()` client that bypasses the shared interceptor: give it
 * the same wait-for-the-code-and-replay-once behaviour, and nothing else. Any
 * other error passes through untouched, so the client's own error mapping is
 * unchanged.
 */
export const installLoginTwoFactorReplay = (client: AxiosInstance): void => {
  // Optional chaining as in apiService: some suites replace axios with a bare mock.
  client?.interceptors?.response?.use?.(undefined, async (error: unknown) => {
    if (
      !axios.isAxiosError(error) ||
      !(await shouldReplayAfterLoginTwoFactor(error))
    ) {
      throw error;
    }
    // The replay's own outcome is the answer. An abandoned screen rejects with
    // the ORIGINAL refusal, so the client's mapping still sees the 403 and code.
    return requestLoginTwoFactorClearance().then(
      () => replayRequest(client, error),
      () => Promise.reject(error)
    );
  });
};

/** True for an Error the shared interceptor shaped from a 403 LOGIN_TWO_FACTOR_REQUIRED. */
export const isLoginTwoFactorRefusal = (error: unknown): boolean => {
  if (typeof error !== "object" || error === null) return false;
  const { status, code } = error as { status?: unknown; code?: unknown };
  return isLoginTwoFactorRequired(
    typeof status === "number" ? status : undefined,
    typeof code === "string" ? code : undefined
  );
};
